import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { SignUpFormContext } from '@providers/SignUpFormProvider/model/SignUpFormContext.ts';
import {
  SignUpFormDefaultValues,
  SignUpFormValues,
  TempTokens,
  TempTokensDefaultValues,
} from '@providers/SignUpFormProvider/model/types.ts';

// The verification screen needs the email to survive a page refresh. Held only in
// memory, a refresh emptied it, the screen bounced to sign-up, and sign-up rejected
// the address as already taken: a dead end reached by simply reloading the page.
// Only the email is stored. This state also carries the password, which must never
// be persisted.
const VERIFY_EMAIL_KEY = 'zlayt.signup.email';

const readStoredEmail = (): string => {
  try {
    return sessionStorage.getItem(VERIFY_EMAIL_KEY) ?? '';
  } catch {
    return '';
  }
};

export const SignUpFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [signUpFormState, setSignUpFormState] = useState<SignUpFormValues>(
    () => ({ ...SignUpFormDefaultValues, email: readStoredEmail() })
  );

  useEffect(() => {
    try {
      if (signUpFormState.email) {
        sessionStorage.setItem(VERIFY_EMAIL_KEY, signUpFormState.email);
      } else {
        sessionStorage.removeItem(VERIFY_EMAIL_KEY);
      }
    } catch {
      // Storage blocked (private mode). The flow still works within one page life.
    }
  }, [signUpFormState.email]);
  const [tempTokens, setTempTokens] = useState<TempTokens>(
    TempTokensDefaultValues
  );

  const backStep = () => {
    setCurrentStep((prevStep) => prevStep - 1);
  };

  const forwardStep = () => {
    setCurrentStep((prevStep) => prevStep + 1);
  };

  return (
    <SignUpFormContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        backStep,
        forwardStep,
        signUpFormState,
        setSignUpFormState,
        tempTokens,
        setTempTokens,
      }}
    >
      {children}
    </SignUpFormContext.Provider>
  );
};

export default SignUpFormProvider;
